import React, { useEffect, useState, useRef } from "react";
import CalibrationApi from "../../../../../api/CalibrationApi";
import { Ri360AutoCalibrationState } from "../../../../../Model/Calibration";
import {
  Ri360CarrierCalibration,
} from "../../../../../Model/StorageConfig";
import AutoCalibrationResultView, {
  EMoveButtonType,
  EMoveButtonState,
} from "./AutoCalibrationResultView";
import { ECarrier } from "../../../../../Model/ECarrier";

type AllMoveButtonState = {
  currentStore: EMoveButtonState;
  currentRetrieve: EMoveButtonState;
  updatedStore: EMoveButtonState;
  updatedRetrieve: EMoveButtonState;
  [key: string]: EMoveButtonState;
};

const getMoveButtonsState = (
  moveButtonsState: AllMoveButtonState,
  useUpdatedValue: boolean
): Map<EMoveButtonType, EMoveButtonState> => {
  if (useUpdatedValue) {
    return new Map<EMoveButtonType, EMoveButtonState>([
      [EMoveButtonType.Store, moveButtonsState.updatedStore],
      [EMoveButtonType.Retrieve, moveButtonsState.updatedRetrieve],
    ]);
  } else {
    return new Map<EMoveButtonType, EMoveButtonState>([
      [EMoveButtonType.Store, moveButtonsState.currentStore],
      [EMoveButtonType.Retrieve, moveButtonsState.currentRetrieve],
    ]);
  }
};

type Props = {
  setAutoCalibrationUpdated: (value: boolean) => void;
  autoCalibrationResults: Ri360AutoCalibrationState[];
  selectedCarrier: ECarrier;
  isPsrBusy: boolean;
};

export default function AutoCalibrationResult({
  setAutoCalibrationUpdated,
  autoCalibrationResults,
  selectedCarrier,
  isPsrBusy,
}: Props) {
  const [selectedHotelIndex, setSelectedHotelIndex] = useState(0);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(0);
  const [storageCalibrations, setStorageCalibrations] = useState(
    [] as Ri360CarrierCalibration[]
  );
  const [autoCalibrationResult, setAutoCalibrationResult] = useState(
    null as Ri360AutoCalibrationState | null
  );
  const [moveButtonsState, setMoveButtonsState] = useState({
    currentStore: EMoveButtonState.Idle,
    currentRetrieve: EMoveButtonState.Idle,
    updatedStore: EMoveButtonState.Idle,
    updatedRetrieve: EMoveButtonState.Idle,
  } as AllMoveButtonState);
  const moveButtonTriggered = useRef(false);

  useEffect(() => {
    CalibrationApi.getStorageCalibration().then((storageCalibration) => {
      setStorageCalibrations(storageCalibration.ri360StorageCalibrations);
    });
  }, []);

  useEffect(() => {
    const selectedAutoCalibrationResult = autoCalibrationResults.filter(
      (ac) => ac.carrier == selectedCarrier
    )[0];
    setAutoCalibrationResult(selectedAutoCalibrationResult);
  }, [autoCalibrationResults, selectedCarrier]);

  useEffect(() => {
    if (isPsrBusy) {
      let newState = { ...moveButtonsState };

      for (let key in moveButtonsState) {
        if (newState[key] !== EMoveButtonState.Loading) {
          newState[key] = EMoveButtonState.Disabled;
        }
      }
      setMoveButtonsState(newState);
    } else {
      setMoveButtonsState({
        currentStore: EMoveButtonState.Idle,
        currentRetrieve: EMoveButtonState.Idle,
        updatedStore: EMoveButtonState.Idle,
        updatedRetrieve: EMoveButtonState.Idle,
      });
    }
  }, [isPsrBusy, moveButtonTriggered]);

  if (storageCalibrations.length === 0) {
    return <div>Loading storage calibrations...</div>;
  }

  if (autoCalibrationResults.length === 0) {
    return <div>Loading auto calibrations result...</div>;
  }

  if (autoCalibrationResult == null) {
    return <div>Loading auto calibration result...</div>;
  }

  const setMoveButtonLoadingActive = (
    moveButtonType: EMoveButtonType,
    useUpdatedValue: boolean
  ) => {
    if (useUpdatedValue) {
      switch (moveButtonType) {
        case EMoveButtonType.Store:
          setMoveButtonsState({ ...moveButtonsState, updatedStore: EMoveButtonState.Loading });
          break;
        case EMoveButtonType.Retrieve:
          setMoveButtonsState({
            ...moveButtonsState,
            updatedRetrieve: EMoveButtonState.Loading,
          });
        default:
          break;
      }
    } else {
      switch (moveButtonType) {
        case EMoveButtonType.Store:
          setMoveButtonsState({ ...moveButtonsState, currentStore: EMoveButtonState.Loading });
          break;
        case EMoveButtonType.Retrieve:
          setMoveButtonsState({
            ...moveButtonsState,
            currentRetrieve: EMoveButtonState.Loading,
          });
        default:
          break;
      }
    }
    moveButtonTriggered.current = !moveButtonTriggered.current;
  };

  return (
    <div className="auto-calibration-result-panel">
      <AutoCalibrationResultView
        selectedCarrier={selectedCarrier}
        storageCalibrations={storageCalibrations}
        autoCalibrationResult={autoCalibrationResult}
        useUpdatedValue={false}
        selectedHotelIndex={selectedHotelIndex}
        setSelectedHotelIndex={setSelectedHotelIndex}
        selectedSlotIndex={selectedSlotIndex}
        setSelectedSlotIndex={setSelectedSlotIndex}
        moveButtonsState={getMoveButtonsState(
          moveButtonsState,
          false
        )}
        SetMoveButtonLoadingActive={setMoveButtonLoadingActive}
      />
      <AutoCalibrationResultView
        selectedCarrier={selectedCarrier}
        storageCalibrations={storageCalibrations}
        autoCalibrationResult={autoCalibrationResult}
        useUpdatedValue={true}
        selectedHotelIndex={selectedHotelIndex}
        setSelectedHotelIndex={setSelectedHotelIndex}
        selectedSlotIndex={selectedSlotIndex}
        setSelectedSlotIndex={setSelectedSlotIndex}
        moveButtonsState={getMoveButtonsState(
          moveButtonsState,
          true
        )}
        SetMoveButtonLoadingActive={setMoveButtonLoadingActive}
      />
    </div>
  );
}
